import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, ChevronDown, Loader2, Play, Save, X } from "lucide-react";

import BuilderReportCard from "./charts/BuilderReportCard";
import RevenueReportCard from "./charts/RevenueReportCard";
import FilterGroupEditor from "./FilterGroupEditor";
import PropertyPicker from "./PropertyPicker";
import StageMultiSelect from "./StageMultiSelect";
import TextListField from "./TextListField";
import {
  fetchFilterOptions,
  fetchObjectProperties,
  fetchReportBuilderMeta,
  previewReportDefinition,
  saveReportDefinition,
} from "../lib/api";

const inputClass =
  "w-full rounded border border-[#cccccc] bg-white px-2.5 py-1.5 text-sm text-[#323c44] transition-colors hover:border-[#7d8b96] focus:border-[#57c1a9] focus:outline-none";
const labelClass = "mb-1 block text-xs font-semibold text-[#4a5b68]";
const hintClass = "mt-1 block text-[11px] leading-snug text-[#7d8b96]";

export const CHART_LABELS = {
  donut: "Donut with centre total",
  bar: "Bar",
  bar_target: "Bar with target line",
  stage_bar: "Stage bar with running total",
  kpi_tiles: "KPI tiles",
  table: "Table",
  line: "Line",
  cohort_bar: "Cohort bar with retention rate",
  cohort_table: "Retention table",
  nrr_table: "Net revenue retention table",
  journey_bar: "Pitch conversion bars",
};

// Dates in a definition are tokens, resolved against whichever fiscal year the
// pack is showing — so one report serves every year.
const DATE_TOKENS = [
  { value: "{{fy_start}}", label: "Start of the fiscal year" },
  { value: "{{fy_end}}", label: "End of the fiscal year" },
  { value: "{{fy_end_exclusive}}", label: "Day after the fiscal year ends" },
  { value: "{{prev_fy_start}}", label: "Start of last fiscal year" },
  { value: "{{prev_fy_end}}", label: "End of last fiscal year" },
  { value: "{{today}}", label: "Today" },
];

// What each report type reads, said plainly, because the controls below differ
// by type and an empty form otherwise looks broken rather than deliberate.
const TYPE_NOTES = {
  record_count: null,
  cohort_retention: null,
  stage_journey: null,
  revenue_retention: null,
  revenue:
    "The Reporting page's MRR report, with its own filters. Month attribution, overlapping "
    + "renewal terms and live RPI work exactly as they do there. The months are the fiscal year "
    + "the pack is showing, and the pack's team narrows the pipelines.",
};

const MEASURE_LABELS = {
  units: "How many clients were retained",
  revenue: "How much of their revenue was retained",
  nrr: "What the same clients went on to spend (NRR)",
};

const VALUE_KIND_LABELS = {
  count: "Count of records",
  sum: "Sum of a property",
  average: "Average of a property",
  distinct_count: "Distinct values of a property",
};

export function blankDefinition(source) {
  return {
    title: "",
    subtitle: "",
    report_type: "record_count",
    source,
    filters: { groups: [] },
    group_by: "",
    value: { kind: "count", property: "" },
    display: {
      chart: "bar", columns: [], colors: {}, order: [], group_map: {},
      target: null, limit: null, show_running_total: false,
    },
    hubspot_report_id: "",
    hubspot_expected_value: null,
    in_board_pack: true,
    section: "",
  };
}

/** A date that is usually a token, but can be pinned to a fixed day. */
function DateTokenField({ value, onChange, label, hint }) {
  const isToken = DATE_TOKENS.some((token) => token.value === value);
  return (
    <label className="block">
      <span className={labelClass}>{label}</span>
      <select
        className={inputClass}
        value={isToken ? value : "__fixed__"}
        onChange={(event) =>
          onChange(event.target.value === "__fixed__" ? "" : event.target.value)
        }
      >
        {DATE_TOKENS.map((token) => (
          <option key={token.value} value={token.value}>
            {token.label}
          </option>
        ))}
        <option value="__fixed__">A fixed date…</option>
      </select>
      {!isToken && (
        <input
          className={`${inputClass} mt-1.5`}
          value={value || ""}
          onChange={(event) => onChange(event.target.value)}
          placeholder="2026-07-01"
        />
      )}
      {hint && <span className={hintClass}>{hint}</span>}
    </label>
  );
}

function currentFiscalYear(fyStartMonth = 7, today = new Date()) {
  const month = today.getMonth() + 1;
  return month >= fyStartMonth ? today.getFullYear() : today.getFullYear() - 1;
}

/**
 * One number box per product the report actually shows.
 *
 * Only the report's own rows are offered, because a target typed against a
 * name that matches no row attaches to nothing: "Candid EC & Essentials = 58"
 * sat beside a row with a different name and showed nowhere. A target
 * saved under such a name is flagged, with a one-click move onto a real row.
 */
function TargetInputs({ products = [], targets = {}, onChange }) {
  const current = targets || {};
  const orphans = Object.keys(current).filter((name) => !products.includes(name));
  const setTarget = (name, raw) => {
    const next = { ...current };
    if (raw === "" || raw === null) delete next[name];
    else if (Number.isFinite(Number(raw))) next[name] = Number(raw);
    onChange(next);
  };
  const moveTarget = (from, to) => {
    if (!to) return;
    const next = { ...current, [to]: current[from] };
    delete next[from];
    onChange(next);
  };
  const removeTarget = (name) => {
    const next = { ...current };
    delete next[name];
    onChange(next);
  };
  return (
    <div className="space-y-2">
      <div className="grid gap-2 sm:grid-cols-2">
        {products.map((name) => (
          <label
            key={name}
            className="flex items-center justify-between gap-3 rounded-lg border border-[#dddddd] bg-white px-3 py-1.5"
          >
            <span className="truncate text-sm text-[#323c44]">{name}</span>
            <input
              type="number"
              min={0}
              step={1}
              inputMode="numeric"
              className="w-24 rounded-full border border-[#cccccc] px-2.5 py-1 text-right text-sm tabular-nums text-[#323c44] focus:border-[#57c1a9] focus:outline-none"
              value={current[name] ?? ""}
              onChange={(event) => setTarget(name, event.target.value)}
              placeholder="None"
              aria-label={`Target for ${name}`}
            />
          </label>
        ))}
      </div>
      {orphans.map((name) => (
        <div
          key={name}
          className="flex flex-wrap items-center gap-2 rounded-lg border border-[#cfa77b] bg-[#fbf6f0] px-3 py-2 text-sm text-[#323c44]"
          data-testid={`orphan-target-${name}`}
        >
          <span className="min-w-0 flex-1">
            A target of <strong>{current[name]}</strong> is saved under "{name}", which is not a
            product in this report, so it shows nowhere.
          </span>
          <select
            defaultValue=""
            onChange={(event) => moveTarget(name, event.target.value)}
            className="rounded-full border border-[#cccccc] bg-white px-2.5 py-1 text-xs text-[#323c44] focus:border-[#57c1a9] focus:outline-none"
            aria-label={`Move the target saved under ${name}`}
          >
            <option value="" disabled>
              Move it to…
            </option>
            {products.map((product) => (
              <option key={product} value={product}>
                {product}
                {current[product] !== undefined ? ` (replaces ${current[product]})` : ""}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => removeTarget(name)}
            className="rounded-full px-2 py-1 text-xs font-semibold text-[#7d8b96] hover:text-[#c2192b]"
          >
            Remove
          </button>
        </div>
      ))}
    </div>
  );
}

/** A titled block, so the form reads as steps rather than one wall of inputs. */
function Section({ title, hint, children }) {
  return (
    <section className="border-t border-[#efefef] pt-5 first:border-0 first:pt-0">
      <h4 className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#323c44]">{title}</h4>
      {hint && <p className="mt-0.5 text-xs text-[#7d8b96]">{hint}</p>}
      <div className="mt-3 space-y-4">{children}</div>
    </section>
  );
}

/** Presentation detail most edits never touch, folded away by default. */
function Advanced({ children, count = 0 }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded border border-[#efefef] bg-[#f5f5f5]">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between px-3 py-2 text-xs font-semibold text-[#4a5b68] hover:text-[#323c44]"
      >
        <span>
          Fine tuning
          {count > 0 && <span className="ml-2 font-normal text-[#7d8b96]">{count} set</span>}
        </span>
        <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="space-y-4 border-t border-[#efefef] px-3 py-3">{children}</div>}
    </div>
  );
}

/**
 * The report definition editor.
 *
 * Shared between the builder tab and the board pack, so a report can be
 * tweaked where it is being read rather than only in a separate screen. Both
 * places edit the same stored definition, so a change made in one shows up in
 * the other and in the printed pack.
 */
export default function ReportDefinitionEditor({
  portalId,
  definition,
  onSaved,
  onCancel,
  sources,
  fyStartYear,
  fyStartMonth = 7,
  teamPipelines = null,
  wrapperClass = "space-y-5 rounded-md border border-[#cccccc] bg-white p-6",
}) {
  const [draft, setDraft] = useState(definition);
  const [meta, setMeta] = useState(null);
  const [properties, setProperties] = useState([]);
  const [propertiesLoading, setPropertiesLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);
  // The Reporting page's filter options, for a revenue report's filters.
  const [revenueOptions, setRevenueOptions] = useState(null);

  useEffect(() => {
    setDraft(definition);
    setPreview(null);
    setPreviewError(null);
    setError(null);
    setSaved(false);
  }, [definition]);

  useEffect(() => {
    fetchReportBuilderMeta().then(setMeta).catch(() => setMeta(null));
  }, []);

  useEffect(() => {
    if (!portalId || draft?.report_type !== "revenue") return;
    fetchFilterOptions(portalId).then(setRevenueOptions).catch(() => setRevenueOptions(null));
  }, [portalId, draft?.report_type]);

  // Property definitions follow the draft's source object, so switching from
  // Deal to Client Service repopulates every picker below.
  useEffect(() => {
    if (!portalId || !draft?.source) {
      setProperties([]);
      return undefined;
    }
    let cancelled = false;
    setPropertiesLoading(true);
    fetchObjectProperties(portalId, draft.source)
      .then((data) => {
        if (!cancelled) setProperties(data.properties || []);
      })
      .catch(() => {
        if (!cancelled) setProperties([]);
      })
      .finally(() => {
        if (!cancelled) setPropertiesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [portalId, draft?.source]);

  const patch = (changes) => {
    setSaved(false);
    setDraft((current) => ({ ...current, ...changes }));
  };
  const patchDisplay = (changes) => {
    setSaved(false);
    setDraft((current) => ({ ...current, display: { ...current.display, ...changes } }));
  };

  const runPreview = async () => {
    if (!portalId || !draft) return;
    // A revenue report previews as the card itself, against the MRR endpoint.
    if (draft.report_type === "revenue") {
      setPreviewError(null);
      setPreview({ ...draft, deferred_to: "revenue", team_pipelines: teamPipelines });
      return;
    }
    setPreviewLoading(true);
    setPreviewError(null);
    try {
      setPreview(await previewReportDefinition(portalId, draft));
    } catch (err) {
      setPreview(null);
      setPreviewError(err?.response?.data?.detail || "Could not run this report.");
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSave = async () => {
    if (!portalId || !draft) return;
    setSaving(true);
    setError(null);
    try {
      const result = await saveReportDefinition(portalId, draft);
      setDraft(result);
      setSaved(true);
      if (onSaved) await onSaved(result);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not save this report.");
    } finally {
      setSaving(false);
    }
  };

  const numericProperties = useMemo(
    () => properties.filter((p) => p.type === "number" || p.type === "currency"),
    [properties],
  );

  const sourceOptions = sources || meta?.sources || [{ value: "deals", label: "Deal" }];
  const isRetention = ["cohort_retention", "revenue_retention"].includes(draft?.report_type);
  // A retention report sharing another's cohort has no filters or grouping of
  // its own: they are set on the report that owns the cohort.
  const isLinked = isRetention && Boolean(draft?.cohort_from);
  const isJourney = draft?.report_type === "stage_journey";
  const isRevenue = draft?.report_type === "revenue";

  // Deal stages come from the Pipelines API, injected onto the property
  // definition as its options (the properties API returns none).
  const stageOptions = useMemo(() => {
    const stageProperty = properties.find(
      (p) => p.name === "dealstage" || p.name === "hs_pipeline_stage",
    );
    return stageProperty?.options || [];
  }, [properties]);

  const dateProperties = useMemo(
    () => properties.filter((p) => p.type === "date" || p.type === "datetime"),
    [properties],
  );

  const patchRevenue = (changes) => {
    setSaved(false);
    setDraft((current) => ({ ...current, revenue: { ...(current.revenue || {}), ...changes } }));
  };

  const patchCohort = (changes) => {
    setSaved(false);
    setDraft((current) => ({ ...current, cohort: { ...(current.cohort || {}), ...changes } }));
  };
  const patchForecast = (changes) =>
    patchCohort({ forecast: { ...(draft.cohort?.forecast || {}), ...changes } });

  const patchJourney = (changes) => {
    setSaved(false);
    setDraft((current) => ({ ...current, journey: { ...(current.journey || {}), ...changes } }));
  };
  const patchOutcome = (index, outcome) =>
    patchJourney({
      outcomes: (draft.journey?.outcomes || []).map((o, i) => (i === index ? outcome : o)),
    });
  const chart = draft?.display?.chart || "bar";
  // The products a retention report can have targets for: its groups, in the
  // report's own order.
  const targetProducts = useMemo(() => {
    const display = draft?.display || {};
    return Array.from(new Set([
      ...(display.order || []),
      ...Object.values(display.group_map || {}),
    ].filter(Boolean)));
  }, [draft?.display]);

  const fineTuningCount =
    (draft?.display?.order?.length ? 1 : 0) +
    (Object.keys(draft?.display?.group_map || {}).length ? 1 : 0) +
    (draft?.display?.limit ? 1 : 0) +
    (draft?.hubspot_report_id ? 1 : 0) +
    (draft?.hubspot_expected_value != null ? 1 : 0);

  if (!draft) return null;

  return (
    <div className={wrapperClass} data-testid="report-definition-editor">
      {/* ── What the report is ─────────────────────────────────────────── */}
      <Section title="Report">
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block">
            <span className={labelClass}>Title</span>
            <input
              className={inputClass}
              value={draft.title || ""}
              onChange={(event) => patch({ title: event.target.value })}
              placeholder="Retained clients overview"
            />
          </label>
          <label className="block">
            <span className={labelClass}>Subtitle</span>
            <input
              className={inputClass}
              value={draft.subtitle || ""}
              onChange={(event) => patch({ subtitle: event.target.value })}
              placeholder="Optional context line"
            />
          </label>
          <label className="block sm:col-span-2">
            <span className={labelClass}>How this is worked out</span>
            <textarea
              className={`${inputClass} min-h-[96px]`}
              value={draft.how_it_works || ""}
              onChange={(event) => patch({ how_it_works: event.target.value })}
              placeholder="Plain words for whoever reads the pack, one point per line."
            />
          </label>
          <label className="block">
            <span className={labelClass}>Section</span>
            <input
              className={inputClass}
              list="board-pack-sections"
              value={draft.section || ""}
              onChange={(event) => patch({ section: event.target.value })}
              placeholder="Client success"
            />
            {/* Free text with suggestions: a new section is created by typing
                one, rather than by editing a list somewhere else. */}
            <datalist id="board-pack-sections">
              {["Client success", "New business", "Pitch conversion", "Product mix", "Retention"].map(
                (name) => <option key={name} value={name} />,
              )}
            </datalist>
            <span className={hintClass}>Groups this report in the board pack.</span>
          </label>
          {!isRevenue && (
          <label className="block">
            <span className={labelClass}>Reads from</span>
            <select
              className={inputClass}
              value={draft.source}
              onChange={(event) =>
                // Property names do not carry across objects, so a source
                // change resets every property-bound field rather than leaving
                // conditions that silently match nothing.
                patch({
                  source: event.target.value,
                  filters: { groups: [] },
                  group_by: "",
                  value: { kind: "count", property: "" },
                  display: { ...draft.display, columns: [], group_map: {} },
                })
              }
            >
              {sourceOptions.map((source) => (
                <option key={source.value} value={source.value}>
                  {source.label}
                </option>
              ))}
            </select>
          </label>
          )}
          {!isRevenue && (
          <label className="block">
            <span className={labelClass}>Shown as</span>
            <select
              className={inputClass}
              value={chart}
              onChange={(event) => patchDisplay({ chart: event.target.value })}
            >
              {(meta?.chart_types || Object.keys(CHART_LABELS)).map((option) => (
                <option key={option} value={option}>
                  {CHART_LABELS[option] || option}
                </option>
              ))}
            </select>
          </label>
          )}
        </div>
      </Section>

      {TYPE_NOTES[draft.report_type] && (
        <p className="rounded border border-[#efefef] bg-[#f5f5f5] px-3 py-2.5 text-xs leading-relaxed text-[#4a5b68]">
          {TYPE_NOTES[draft.report_type]}
        </p>
      )}

      {/* ── Which records ──────────────────────────────────────────────── */}
      {!isRevenue && !isLinked && (
      <Section
        title="Which records"
        hint="A record counts if it satisfies every condition in any one group."
      >
        {propertiesLoading ? (
          <p className="flex items-center gap-2 text-sm text-[#7d8b96]">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading properties…
          </p>
        ) : (
          <FilterGroupEditor
            filters={draft.filters}
            properties={properties}
            operators={meta?.operators || []}
            onChange={(filters) => patch({ filters })}
          />
        )}
      </Section>
      )}

      {/* ── Revenue: the Reporting page's MRR filters ───────────────────── */}
      {isRevenue && (
        <Section
          title="Which revenue"
          hint="Leave a list empty to include everything."
        >
          <div className="grid gap-4 sm:grid-cols-2">
            {[
              ["pipelines", "Pipelines", revenueOptions?.pipelines],
              ["stages", "Deal stages", revenueOptions?.stages],
              ["exclude_stages", "Leave out deal stages", revenueOptions?.stages],
              ["products", "Products", revenueOptions?.products],
              ["owners", "Deal owners", revenueOptions?.owners],
              ["billing_frequencies", "Billing frequencies", revenueOptions?.billing_frequencies],
            ].map(([field, label, options]) => (
              <div key={field}>
                <span className={labelClass}>{label}</span>
                <StageMultiSelect
                  options={(options || []).map((option) => ({ value: option, label: option }))}
                  value={draft.revenue?.[field] || []}
                  onChange={(values) => patchRevenue({ [field]: values })}
                  placeholder="All"
                  testId={`revenue-${field}`}
                />
              </div>
            ))}
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <label className="block">
              <span className={labelClass}>Shown as</span>
              <select
                className={inputClass}
                value={draft.revenue?.view || "stacked"}
                onChange={(event) => patchRevenue({ view: event.target.value })}
              >
                <option value="stacked">Stacked bars</option>
                <option value="grouped">Grouped bars</option>
                <option value="table">Table</option>
              </select>
            </label>
            <label className="block">
              <span className={labelClass}>Probability from (%)</span>
              <input
                className={inputClass}
                type="number"
                value={draft.revenue?.probability_min ?? ""}
                onChange={(event) =>
                  patchRevenue({
                    probability_min: event.target.value === "" ? null : Number(event.target.value),
                  })
                }
                placeholder="Any"
              />
            </label>
            <label className="block">
              <span className={labelClass}>Probability to (%)</span>
              <input
                className={inputClass}
                type="number"
                value={draft.revenue?.probability_max ?? ""}
                onChange={(event) =>
                  patchRevenue({
                    probability_max: event.target.value === "" ? null : Number(event.target.value),
                  })
                }
                placeholder="Any"
              />
            </label>
          </div>

          <div className="grid gap-2 sm:grid-cols-2">
            {[
              ["collapse_products", "Roll products up to their reporting group", "CRS Australia counts as CRS, every Candid as Candid ATS."],
              ["include_one_time", "Include one-time revenue", "Shown as its own band, as on the Reporting page."],
              ["exclude_non_xero", "Only revenue invoiced through Xero", null],
              ["show_growth", "Say how much MRR has grown over the year", null],
            ].map(([field, label, hint]) => (
              <label key={field} className="flex items-start gap-2 text-sm text-[#323c44]">
                <input
                  type="checkbox"
                  checked={
                    field === "show_growth"
                      ? draft.revenue?.show_growth !== false
                      : Boolean(draft.revenue?.[field])
                  }
                  onChange={(event) => patchRevenue({ [field]: event.target.checked })}
                />
                <span>
                  {label}
                  {hint && <span className={hintClass}>{hint}</span>}
                </span>
              </label>
            ))}
          </div>
        </Section>
      )}

      {/* ── Cohort: who is counted, when, and what counts as a loss ────── */}
      {isRetention && (
        <Section
          title="Cohort"
          hint="Client services live on the start date, measured again on the second."
        >
          <div className="grid gap-4 sm:grid-cols-3">
            <label className="block">
              <span className={labelClass}>Measured by</span>
              {draft.report_type === "revenue_retention" ? (
                <p className="py-1.5 text-sm text-[#323c44]">{MEASURE_LABELS.nrr}</p>
              ) : (
                <select
                  className={inputClass}
                  value={draft.measure || "units"}
                  onChange={(event) => patch({ measure: event.target.value })}
                >
                  <option value="units">{MEASURE_LABELS.units}</option>
                  <option value="revenue">{MEASURE_LABELS.revenue}</option>
                </select>
              )}
            </label>
          </div>

          <label className="flex items-start gap-2 text-sm text-[#323c44]">
            <input
              type="checkbox"
              checked={isLinked}
              onChange={(event) =>
                patch(
                  event.target.checked
                    ? { cohort_from: "retention-by-unit" }
                    : {
                        cohort_from: null,
                        cohort: draft.cohort?.start
                          ? draft.cohort
                          : { start: "{{fy_start}}", as_of: "{{today}}" },
                        group_by: draft.group_by || "products",
                      },
                )
              }
            />
            <span>
              Use the same cohort as Retention by unit
              <span className={hintClass}>
                Who is counted, how products are grouped and what counts as a loss are then set on
                that report, so the retention reports always count the same clients and the same
                losses.
              </span>
            </span>
          </label>

          {!isLinked && (
            <>
              <div className="grid gap-4 sm:grid-cols-2">
                <DateTokenField
                  label="Cohort taken on"
                  value={draft.cohort?.start || ""}
                  onChange={(start) => patchCohort({ start })}
                />
                <DateTokenField
                  label="Measured on"
                  value={draft.cohort?.as_of || ""}
                  onChange={(as_of) => patchCohort({ as_of })}
                  hint="Today counts whoever is still live on the day the report runs. A finished year is measured at its end."
                />
              </div>

              <div className="grid gap-4 sm:grid-cols-3">
                <div>
                  <span className={labelClass}>Start date property</span>
                  <PropertyPicker
                    properties={dateProperties}
                    value={draft.cohort?.effective_date_property || ""}
                    onChange={(name) => patchCohort({ effective_date_property: name })}
                    testId="effective-date-property"
                  />
                </div>
                <div>
                  <span className={labelClass}>Churn date property</span>
                  <PropertyPicker
                    properties={dateProperties}
                    value={draft.cohort?.churn_date_property || ""}
                    onChange={(name) => patchCohort({ churn_date_property: name })}
                    testId="churn-date-property"
                  />
                </div>
                <div>
                  <span className={labelClass}>Company name property</span>
                  <PropertyPicker
                    properties={properties}
                    value={draft.cohort?.company_property || ""}
                    onChange={(name) => patchCohort({ company_property: name })}
                    testId="company-property"
                  />
                  <span className={hintClass}>
                    Only used where a client service has no associated company.
                  </span>
                </div>
              </div>

              <div>
                <span className={labelClass}>Replaced by a renewal when in stage</span>
                <StageMultiSelect
                  options={stageOptions}
                  value={draft.cohort?.superseded_stages || ["Renewed"]}
                  onChange={(stages) => patchCohort({ superseded_stages: stages })}
                  placeholder="Renewed"
                  testId="superseded-stages"
                />
                <span className={hintClass}>
                  A record in one of these stages ends when the next record for the same client and
                  product starts, so a renewed client counts once.
                </span>
              </div>

              <div className="rounded border border-[#efefef] bg-[#f5f5f5] p-3">
                <p className="text-xs font-semibold text-[#4a5b68]">Forecast losses</p>
                <p className={hintClass}>
                  The forecast takes off clients already lost, with a churn date ahead, or forecast
                  not to renew. Clients unlikely to renew are shown on their own line, not taken off.
                </p>
                <div className="mt-3 grid gap-4 sm:grid-cols-2">
                  <div>
                    <span className={labelClass}>Not renewing</span>
                    <PropertyPicker
                      properties={properties}
                      value={draft.cohort?.forecast?.not_renewing_property || ""}
                      onChange={(name) => patchForecast({ not_renewing_property: name })}
                      testId="not-renewing-property"
                    />
                    <TextListField
                      className={`${inputClass} mt-1.5 font-mono text-xs`}
                      rows={2}
                      value={draft.cohort?.forecast?.not_renewing_values || []}
                      toText={(list) => (list || []).join("\n")}
                      fromText={(text) => text.split("\n").map((v) => v.trim()).filter(Boolean)}
                      onChange={(values) => patchForecast({ not_renewing_values: values })}
                      placeholder={"No"}
                    />
                  </div>
                  <div>
                    <span className={labelClass}>Unlikely to renew</span>
                    <PropertyPicker
                      properties={properties}
                      value={draft.cohort?.forecast?.unlikely_property || ""}
                      onChange={(name) => patchForecast({ unlikely_property: name })}
                      testId="unlikely-property"
                    />
                    <TextListField
                      className={`${inputClass} mt-1.5 font-mono text-xs`}
                      rows={2}
                      value={draft.cohort?.forecast?.unlikely_values || []}
                      toText={(list) => (list || []).join("\n")}
                      fromText={(text) => text.split("\n").map((v) => v.trim()).filter(Boolean)}
                      onChange={(values) => patchForecast({ unlikely_values: values })}
                      placeholder={"Unlikely\nChurned"}
                    />
                  </div>
                </div>
              </div>
            </>
          )}

          {draft.measure !== "revenue" && draft.report_type === "cohort_retention" && (
            <div>
              <span className={labelClass}>Retention targets</span>
              <TargetInputs
                products={targetProducts}
                targets={draft.display?.targets || {}}
                onChange={(targets) => patchDisplay({ targets })}
              />
              <span className={hintClass}>
                The number of clients per product the year should end with. Leave a box empty for
                no target. The products are this report's own rows; to add one, change how products
                are grouped under Fine tuning.
              </span>
            </div>
          )}
        </Section>
      )}

      {/* ── Pitch conversion: which stages count, and what they roll into ── */}
      {isJourney && (
        <Section
          title="Pitch conversion"
          hint="Deals that reached any of these stages at any time, grouped by where they are now."
        >
          <div>
            <span className={labelClass}>Pitch stages</span>
            <StageMultiSelect
              options={stageOptions}
              value={draft.journey?.entered_stages || []}
              onChange={(stages) => patchJourney({ entered_stages: stages })}
              placeholder="Choose the stages that count as a pitch…"
              testId="pitch-stages"
            />
            <span className={hintClass}>
              A deal counts once it has entered any of these, even if it has since moved on.
            </span>
          </div>

          <div className="rounded border border-[#efefef] bg-[#f5f5f5] p-3">
            <label className="flex items-start gap-2 text-sm text-[#323c44]">
              <input
                type="checkbox"
                checked={Boolean(draft.journey?.closed_only)}
                onChange={(event) => patchJourney({ closed_only: event.target.checked })}
              />
              <span>
                Only deals won or lost (a win rate)
                <span className={hintClass}>
                  Counts a deal when it closes, however long ago it was pitched. Open deals, including
                  those at contract, are left out.
                </span>
              </span>
            </label>
            {draft.journey?.closed_only && (
              <div className="mt-3 grid gap-4 sm:grid-cols-2">
                <DateTokenField
                  label="Closed from"
                  value={draft.journey?.closed_from || ""}
                  onChange={(closed_from) => patchJourney({ closed_from })}
                />
                <DateTokenField
                  label="Closed to"
                  value={draft.journey?.closed_to || ""}
                  onChange={(closed_to) => patchJourney({ closed_to })}
                />
              </div>
            )}
            <label className="mt-3 flex items-start gap-2 text-sm text-[#323c44]">
              <input
                type="checkbox"
                checked={Boolean(draft.journey?.compare_previous_year)}
                onChange={(event) => patchJourney({ compare_previous_year: event.target.checked })}
              />
              <span>
                Show last year beside it
                <span className={hintClass}>The same period last year, and the whole of last year.</span>
              </span>
            </label>
            <label className="mt-3 flex items-start gap-2 text-sm text-[#323c44]">
              <input
                type="checkbox"
                checked={Boolean(draft.journey?.old_stages_count_as_pitch)}
                onChange={(event) => patchJourney({ old_stages_count_as_pitch: event.target.checked })}
              />
              <span>
                Count deals through deleted stages as pitched
                <span className={hintClass}>
                  Deals from before the pipelines were rebuilt went through stages that no longer exist, so
                  HubSpot has no pitch date for them. Applies to won and lost deals alike.
                </span>
              </span>
            </label>
          </div>

          <div className="space-y-3">
            <span className={labelClass}>Outcomes</span>
            {(draft.journey?.outcomes || []).map((outcome, index) => (
              <div key={index} className="rounded border border-[#efefef] bg-[#f5f5f5] p-3">
                <div className="mb-2 flex items-center gap-2">
                  <input
                    className={`${inputClass} max-w-[180px]`}
                    value={outcome.label || ""}
                    onChange={(event) =>
                      patchOutcome(index, { ...outcome, label: event.target.value })
                    }
                    placeholder="Won"
                  />
                  <button
                    type="button"
                    onClick={() =>
                      patchJourney({
                        outcomes: draft.journey.outcomes.filter((_, i) => i !== index),
                      })
                    }
                    className="rounded p-1 text-[#7d8b96] hover:bg-red-50 hover:text-red-600"
                    title="Remove outcome"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
                <StageMultiSelect
                  options={stageOptions}
                  value={outcome.stages || []}
                  onChange={(stages) => patchOutcome(index, { ...outcome, stages })}
                  placeholder="Stages that count as this outcome…"
                  testId={`outcome-${index}`}
                />
              </div>
            ))}
            <button
              type="button"
              onClick={() =>
                patchJourney({
                  outcomes: [...(draft.journey?.outcomes || []), { label: "", stages: [] }],
                })
              }
              className="inline-flex items-center gap-1.5 rounded-full border border-[#cccccc] bg-white px-3 py-1.5 text-xs font-semibold text-[#323c44] hover:border-[#57c1a9] hover:text-[#119996]"
            >
              Add an outcome
            </button>
            <p className={hintClass}>
              Anything not listed shows as Other rather than leaving the total. Win rate is the
              Won outcome against Won plus Lost, closed only.
            </p>
          </div>
        </Section>
      )}

      {/* ── What is measured ───────────────────────────────────────────── */}
      {!isJourney && !isRevenue && !isLinked && (
      <Section title="What is measured">
        <div className="grid gap-4 sm:grid-cols-3">
          <div>
            <span className={labelClass}>Split by</span>
            <PropertyPicker
              properties={properties}
              value={draft.group_by || ""}
              onChange={(name) => patch({ group_by: name })}
              placeholder="No grouping"
              testId="group-by"
            />
          </div>

          {isRetention ? null : (
            <>
              <label className="block">
                <span className={labelClass}>Measured by</span>
                <select
                  className={inputClass}
                  value={draft.value?.kind || "count"}
                  onChange={(event) => patch({ value: { ...draft.value, kind: event.target.value } })}
                >
                  {(meta?.value_kinds || Object.keys(VALUE_KIND_LABELS)).map((kind) => (
                    <option key={kind} value={kind}>
                      {VALUE_KIND_LABELS[kind] || kind}
                    </option>
                  ))}
                </select>
              </label>
              {draft.value?.kind !== "count" && (
                <div>
                  <span className={labelClass}>Of property</span>
                  <PropertyPicker
                    properties={draft.value?.kind === "distinct_count" ? properties : numericProperties}
                    value={draft.value?.property || ""}
                    onChange={(name) => patch({ value: { ...draft.value, property: name } })}
                    testId="value-property"
                  />
                </div>
              )}
            </>
          )}
        </div>

        {chart === "table" && (
          <div>
            <span className={labelClass}>Table columns</span>
            <PropertyPicker
              properties={properties}
              value={draft.display?.columns || []}
              onChange={(names) => patchDisplay({ columns: names })}
              placeholder="Choose columns…"
              multiple
              testId="table-columns"
            />
            {draft.display?.columns?.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {draft.display.columns.map((name) => (
                  <span
                    key={name}
                    className="inline-flex items-center gap-1 rounded bg-[#efefef] px-2 py-0.5 text-[11px] text-[#4a5b68]"
                  >
                    {properties.find((p) => p.name === name)?.label || name}
                    <button
                      type="button"
                      onClick={() =>
                        patchDisplay({ columns: draft.display.columns.filter((c) => c !== name) })
                      }
                      className="text-[#a9b3bb] hover:text-red-600"
                      aria-label={`Remove ${name}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        )}

        {chart === "bar_target" && (
          <label className="block sm:w-1/3">
            <span className={labelClass}>Target</span>
            <input
              className={inputClass}
              type="number"
              value={draft.display?.target ?? ""}
              onChange={(event) =>
                patchDisplay({ target: event.target.value === "" ? null : Number(event.target.value) })
              }
              placeholder="455000"
            />
            <span className={hintClass}>Also editable on the report card itself.</span>
          </label>
        )}
      </Section>
      )}

      {/* ── Presentation detail and reconciliation ─────────────────────── */}
      <Advanced count={fineTuningCount}>
        {draft.group_by && (
          <>
            <label className="block">
              <span className={labelClass}>Series order</span>
              <TextListField
                className={`${inputClass} font-mono text-xs`}
                value={draft.display?.order || []}
                toText={(order) => (order || []).join("\n")}
                fromText={(text) => text.split("\n").map((v) => v.trim()).filter(Boolean)}
                onChange={(order) => patchDisplay({ order })}
                placeholder={"Churned\nRetained\nInitial term\nNew contract"}
              />
              <span className={hintClass}>
                One label per line. Anything not listed follows, largest first.
              </span>
            </label>

            {!isLinked && (
            <label className="block">
              <span className={labelClass}>Rename or merge groups</span>
              <TextListField
                className={`${inputClass} font-mono text-xs`}
                value={draft.display?.group_map || {}}
                toText={(map) =>
                  Object.entries(map || {})
                    .map(([from, to]) => `${from} = ${to}`)
                    .join("\n")
                }
                fromText={(text) => {
                  const map = {};
                  for (const line of text.split("\n")) {
                    const index = line.indexOf("=");
                    if (index === -1) continue;
                    const from = line.slice(0, index).trim();
                    const to = line.slice(index + 1).trim();
                    if (from && to) map[from] = to;
                  }
                  return map;
                }}
                onChange={(group_map) => patchDisplay({ group_map })}
                placeholder={"0.75 = 75%\n0.9 = 90%"}
              />
              <span className={hintClass}>
                Several values can share one bar. Anything unmapped keeps its own label.
              </span>
            </label>
            )}

            <label className="block sm:w-1/3">
              <span className={labelClass}>Sort by</span>
              <select
                className={inputClass}
                value={draft.display?.sort_groups || ""}
                onChange={(event) => patchDisplay({ sort_groups: event.target.value || null })}
              >
                <option value="">Default</option>
                <option value="value_desc">Value, high to low</option>
                <option value="value_asc">Value, low to high</option>
                <option value="label_asc">Name, A to Z</option>
                <option value="label_desc">Name, Z to A</option>
              </select>
              <span className={hintClass}>After the series order above. Tables sort by clicking a heading.</span>
            </label>

            <label className="block sm:w-1/3">
              <span className={labelClass}>Show top</span>
              <input
                className={inputClass}
                type="number"
                min={1}
                value={draft.display?.limit ?? ""}
                onChange={(event) =>
                  patchDisplay({ limit: event.target.value === "" ? null : Number(event.target.value) })
                }
                placeholder="All"
              />
              <span className={hintClass}>The rest fold into Other, so the total still adds up.</span>
            </label>
          </>
        )}

        {chart === "table" && (
          <div>
            <span className={labelClass}>One row per</span>
            <PropertyPicker
              properties={properties}
              value={draft.display?.one_row_per || ""}
              onChange={(name) => patchDisplay({ one_row_per: name || null })}
              placeholder="Every record"
              testId="one-row-per"
            />
            <span className={hintClass}>
              Records sharing this value show once, keeping the one with the most filled in. Use it
              for one row per company when a company has several deals.
            </span>
          </div>
        )}

        {!isRevenue && (
        <div>
          <span className={labelClass}>Columns when a group is opened</span>
          <PropertyPicker
            properties={properties}
            value={draft.display?.drilldown_columns || []}
            onChange={(names) => patchDisplay({ drilldown_columns: names })}
            placeholder="Whatever the report reads"
            multiple
            testId="drilldown-columns"
          />
          {draft.display?.drilldown_columns?.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {draft.display.drilldown_columns.map((name) => (
                <span
                  key={name}
                  className="inline-flex items-center gap-1 rounded bg-[#efefef] px-2 py-0.5 text-[11px] text-[#4a5b68]"
                >
                  {properties.find((p) => p.name === name)?.label || name}
                  <button
                    type="button"
                    onClick={() =>
                      patchDisplay({
                        drilldown_columns: draft.display.drilldown_columns.filter((c) => c !== name),
                      })
                    }
                    className="text-[#a9b3bb] hover:text-red-600"
                    aria-label={`Remove ${name}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
          <span className={hintClass}>
            Left empty, a drilldown shows the properties that decided the answer.
          </span>
        </div>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block">
            <span className={labelClass}>HubSpot report id</span>
            <input
              className={inputClass}
              value={draft.hubspot_report_id || ""}
              onChange={(event) => patch({ hubspot_report_id: event.target.value })}
              placeholder="369308847"
            />
          </label>
          <label className="block">
            <span className={labelClass}>HubSpot's figure</span>
            <input
              className={inputClass}
              type="number"
              value={draft.hubspot_expected_value ?? ""}
              onChange={(event) =>
                patch({
                  hubspot_expected_value: event.target.value === "" ? null : Number(event.target.value),
                })
              }
              placeholder="For the reconciliation view"
            />
          </label>
        </div>

        <label className="flex items-center gap-2 text-sm text-[#323c44]">
          <input
            type="checkbox"
            checked={draft.in_board_pack !== false}
            onChange={(event) => patch({ in_board_pack: event.target.checked })}
          />
          Include in the board pack
        </label>
      </Advanced>

      {/* ── Actions ────────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-2 border-t border-[#efefef] pt-5">
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || !draft.title}
          className="inline-flex items-center gap-2 rounded-full bg-[#50e3c2] px-4 py-2 text-sm font-semibold text-[#323c44] hover:bg-[#57c1a9] disabled:opacity-50 uppercase tracking-[0.12em]"
          data-testid="save-report-definition"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Save
        </button>
        <button
          type="button"
          onClick={runPreview}
          disabled={previewLoading}
          className="inline-flex items-center gap-2 rounded-full border border-[#cccccc] bg-white px-3 py-2 text-sm font-semibold text-[#323c44] hover:border-[#57c1a9] hover:text-[#119996] disabled:opacity-50"
        >
          {previewLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
          Preview
        </button>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="inline-flex items-center gap-2 rounded px-3 py-2 text-sm font-semibold text-[#7d8b96] hover:text-[#323c44]"
          >
            <X className="h-4 w-4" /> Done
          </button>
        )}
        {saved && !saving && <span className="text-xs font-semibold text-emerald-600">Saved</span>}
      </div>

      {error && (
        <p className="flex items-start gap-2 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          {error}
        </p>
      )}
      {previewError && (
        <p className="flex items-start gap-2 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          {previewError}
        </p>
      )}
      {preview && preview.deferred_to === "revenue" && (
        <RevenueReportCard
          portalId={portalId}
          report={preview}
          fyStartYear={fyStartYear || currentFiscalYear(fyStartMonth)}
          fyStartMonth={fyStartMonth}
        />
      )}
      {preview && preview.deferred_to !== "revenue" && <BuilderReportCard result={preview} />}
    </div>
  );
}
